import { Link } from "wouter";
import { ArrowRight, ArrowUpRight, MapPin, User, Calendar } from "lucide-react";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { StatusDot } from "../ui/status-dot";
import { CopyButton } from "../ui/copy-button";
import { fmtCurrency, fmtDateTime } from "../../lib/format";
import { Card } from "../ui/card";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { isDriverInfoOverdue } from "../../../../shared/order-rules";
import type { ServiceType } from "../../../../shared/const";

interface OrderRow {
  id: number;
  orderNumber: string;
  agencyId: number;
  fleetId: number | null;
  region: "taiwan" | "hongkong";
  createdVia?: "agency_self" | "dispatcher_proxy";
  serviceType: ServiceType;
  vehicleType: string;
  pickupTime: number;
  pickupLocation: string;
  /** 178 反饋 A2/C5：卡片顯示起迄點（點對點以外可能為空） */
  dropoffLocation?: string | null;
  passengerName: string;
  passengerCount: number;
  finalPrice: string | null;
  fleetPayout: string | null;
  fleetPayoutCurrency: "TWD" | "HKD" | "USD" | null;
  currency: "TWD" | "HKD" | "USD";
  status: "pending" | "quoting" | "quoted" | "rejected_quote" | "confirmed" | "dispatching" | "rejected_dispatch" | "active" | "completed" | "cancelled";
  agencyName?: string | null;
  fleetName?: string | null;
  createdAt: Date;
  /** 20260611 §5：出發前 24h 未回報司機資訊 → 列表標紅（178 反饋 C3：放寬到四角色） */
  driverInfoSubmittedAt?: number | null;
  /** 178 反饋 A2/C5：列表卡片顯示司機資訊（四方可見，不在金額裁剪範圍） */
  driverName?: string | null;
  driverPhone?: string | null;
  vehiclePlate?: string | null;
}

export function OrderListItem({ order }: { order: OrderRow }) {
  const { t } = useLanguage();
  const { user } = useAuth();

  const counterParty = (() => {
    if (!user) return null;
    if (user.role === "agency") return order.fleetName ?? t({ "zh-TW": "尚未指派車隊", "en-US": "No fleet yet", "zh-CN": "尚未指派车队" });
    if (user.role === "fleet") return order.agencyName ?? "—";
    return `${order.agencyName ?? "—"} → ${order.fleetName ?? t({ "zh-TW": "待派遣", "en-US": "to be dispatched", "zh-CN": "待派遣" })}`;
  })();

  // 金額依角色：後端已裁剪（供應方 finalPrice=null、保留 fleetPayout），
  // 故供應方顯示「車隊實得」、需求方/平台方顯示「報價總額」，與詳情頁口徑一致。
  const amount = order.finalPrice ?? order.fleetPayout ?? null;
  const amountCurrency = order.finalPrice ? order.currency : (order.fleetPayoutCurrency ?? order.currency);

  // 20260611 §5 ＋ 178 反饋 C3：出發 <24h 且 active 且未回報司機資訊 → 四角色列表都亮警示；
  // fleet（責任方）另外整卡紅色視覺，讓車隊一眼看到
  const driverInfoAlert = isDriverInfoOverdue(order.status, order.pickupTime, order.driverInfoSubmittedAt, Date.now());
  const fleetOverdueHighlight = driverInfoAlert && user?.role === "fleet";
  const driverInfoAlertLabel = t({ "zh-TW": "司機資訊未回報", "en-US": "Driver info not reported", "zh-CN": "司机资讯未回报" });

  // 區域 rail：台灣翡翠、香港琥珀 — 加一點溫度區分（fleet 逾期紅色視覺時改為警示紅，避免雙色條打架）
  const rail = order.region === "taiwan" ? "bg-primary" : "bg-warm";
  const serviceTint =
    order.serviceType === "charter"
      ? "text-primary-soft-foreground"
      : "text-warm-soft-foreground";

  return (
    <Link href={`/orders/${order.id}`}>
      <Card
        className={`group relative cursor-pointer overflow-hidden transition-all hover:border-border-strong hover:shadow-[0_2px_8px_-2px_oklch(0%_0_0/0.06)] ${
          // 178 反饋 C3（標紅）：fleet 逾期未回報司機資訊 → 淡紅背景；左緣紅色由下方 rail 一處負責
          //（色票在 :root，named utility 不生效，用 var() 寫法）
          fleetOverdueHighlight ? "bg-[var(--color-destructive-soft)]" : ""
        }`}
      >
        <span
          className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full ${fleetOverdueHighlight ? "bg-[var(--color-destructive)]" : rail}`}
          aria-hidden
        />
        <div className="grid grid-cols-1 gap-3 p-4 pl-5 sm:grid-cols-[1fr_auto] sm:items-center">
          {/* main */}
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {driverInfoAlert && (
                <span title={driverInfoAlertLabel} aria-label={driverInfoAlertLabel} className="inline-flex">
                  <StatusDot tone="danger" pulse />
                </span>
              )}
              <span className="font-mono text-[12px] font-medium text-foreground">{order.orderNumber}</span>
              {/* 178 反饋 A17：訂單編號一鍵複製（按鈕內已擋 Link 導航） */}
              <CopyButton
                text={order.orderNumber}
                label={t({ "zh-TW": "複製訂單編號", "en-US": "Copy order number", "zh-CN": "复制订单编号" })}
              />
              <OrderStatusBadge status={order.status} size="sm" />
              {order.createdVia === "dispatcher_proxy" && (
                <span className="rounded bg-primary-soft px-1.5 py-0.5 text-[10px] font-medium text-primary-soft-foreground">
                  {t({ "zh-TW": "平台代建", "en-US": "By platform", "zh-CN": "平台代建" })}
                </span>
              )}
              <span className="font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
                {order.region === "taiwan" ? "TW" : "HK"}
              </span>
              <span className={`font-mono text-[12px] uppercase tracking-wider ${serviceTint}`}>
                {order.serviceType === "charter"
                  ? t({ "zh-TW": "包車", "en-US": "Charter", "zh-CN": "包车" })
                  : t({ "zh-TW": "接送", "en-US": "Transfer", "zh-CN": "接送" })}
              </span>
            </div>

            <div className="grid grid-cols-1 gap-1.5 text-[13px] sm:grid-cols-[1fr_auto] sm:items-baseline">
              {/* 178 反饋 A2/C5：起迄點並列（無迄點時只顯示上車點） */}
              <div className="flex min-w-0 items-center gap-1.5 text-foreground/90">
                <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate">{order.pickupLocation}</span>
                {order.dropoffLocation && (
                  <>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground/60" aria-hidden />
                    <span className="truncate">{order.dropoffLocation}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {fmtDateTime(order.pickupTime)}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {order.passengerCount}
                </span>
              </div>
            </div>

            <p className="text-[12px] text-muted-foreground truncate">{counterParty}</p>

            {/* 178 反饋 A2/C5：司機資訊列 — 已回報顯示 姓名 · 電話 · 車牌；active 未回報顯示灰色斜體提示；
                其他狀態（pendingQuote 等派遣前變體）無司機資料時不顯示，避免無意義空列 */}
            {order.driverName ? (
              <p className="flex flex-wrap items-center gap-x-1.5 text-[12px] text-muted-foreground">
                <span>{t({ "zh-TW": "司機", "en-US": "Driver", "zh-CN": "司机" })}</span>
                <span className="font-medium text-foreground/80">{order.driverName}</span>
                {order.driverPhone && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="numeric">{order.driverPhone}</span>
                  </>
                )}
                {order.vehiclePlate && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="font-mono uppercase">{order.vehiclePlate}</span>
                  </>
                )}
              </p>
            ) : order.status === "active" ? (
              <p className="text-[12px] italic text-muted-foreground">
                {t({ "zh-TW": "司機資訊：尚未回報", "en-US": "Driver info: not reported yet", "zh-CN": "司机资讯：尚未回报" })}
              </p>
            ) : null}
          </div>

          {/* price */}
          <div className="flex items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
            <p className="numeric font-display text-lg font-medium text-foreground">
              {fmtCurrency(amount, amountCurrency)}
            </p>
            <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground/40 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function OrderListEmpty({ message }: { message?: string }) {
  const { t } = useLanguage();
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center">
      <p className="font-display text-base text-muted-foreground">
        {message ?? t({ "zh-TW": "目前沒有訂單", "en-US": "No orders yet", "zh-CN": "目前没有订单" })}
      </p>
    </div>
  );
}
